import { FormEvent, useEffect, useRef, useState } from "react";
import { Input } from "../../components/ui/input";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Button } from "../../components/ui/button";
import BackIcon from "../../components/back-icon";
import { Label } from "../../components/ui/label";
import { useGetOTP } from "../../lib/hooks/auth/use-get-otp";
import { useToast } from "../../components/ui/use-toast";

import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "../../components/ui/input-otp";
import { useVerifyOTP, useFetchJWT } from "../../lib/hooks/auth/use-verify-otp";
import { Loader2 } from "lucide-react";
import { useAuth } from "../../contexts/auth-context";
import { useDoctor } from "../../lib/hooks/doctors/use-doctor";
import { getCookie } from "../../contexts/auth-context"
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { getCountryCallingCode } from 'react-phone-number-input';
import { isValidPhoneNumber } from 'libphonenumber-js';

const phoneInputStyles = `
  .PhoneInput {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 0.5rem;
    padding: 0.75rem 1rem;
    transition: all 0.2s ease;
  }
  .PhoneInput:hover {
    border-color: #d1d5db;
  }
  .PhoneInput:focus-within {
    border-color: #6b7280;
    box-shadow: 0 0 0 2px rgba(107, 114, 128, 0.2);
  }
  .PhoneInputInput {
    flex: 1;
    border: none;
    outline: none;
    font-size: 0.875rem;
    line-height: 1.5;
    color: #111827;
    background: transparent;
  }
  .PhoneInputCountry {
    position: relative;
  }
  .PhoneInputCountrySelect {
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    background: transparent;
    border: none;
    padding-right: 1.5rem;
    padding-left: 0.25rem;
    cursor: pointer;
  }
  .PhoneInputCountrySelect:hover {
    background: #f3f4f6;
  }
  .PhoneInputCountrySelect:focus {
    outline: none;
    border-color: #6b7280;
    box-shadow: 0 0 0 2px rgba(107, 114, 128, 0.2);
  }
  @media (max-width: 640px) {
    .PhoneInput {
      padding: 0.5rem 0.75rem;
    }
    .PhoneInputInput {
      font-size: 0.75rem;
    }
    .PhoneInputCountrySelect {
      padding: 0.25rem 1.25rem 0.25rem 0.5rem;
      font-size: 0.75rem;
    }
  }
`;
interface PopverifyPatientProps {
  onClose: () => void;
}

function PopverifyPatient({ onClose }: PopverifyPatientProps) {
  const { toast } = useToast();
  const { setMobile, setToken } = useAuth()

  const params = useParams();
  const professionalId = params.professionalId;

  const { doctor, isLoading } = useDoctor(professionalId);




  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectUrl = searchParams.get("redirect") ?? "/doctors";

  const [mobile, setMobileVal] = useState<string | undefined>(undefined);
  const isMobileValid = mobile ? isValidPhoneNumber(mobile) : false;
  const [otp, setOtp] = useState("");
  const [otpSession, setOtpSession] = useState(false);
  const [isResendDisabled, setIsResendDisabled] = useState(true);
  const [timer, setTimer] = useState(60);

  const otpInputRef = useRef<HTMLInputElement>(null);

  const { getOTP, data, isPending: isGettingOTP } = useGetOTP();
  const { verifyOTP, isPending: isVerifying } = useVerifyOTP();
  const { fetchJWT, isPending: isFetchingJWT } = useFetchJWT()




  useEffect(() => {
    if (!isMobileValid) {
      setOtp("");
      setOtpSession(false);
    }
  }, [mobile]);

  useEffect(() => {
    if (isResendDisabled && timer > 0) {
      const interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);

      return () => clearInterval(interval);
    }

    if (timer === 0) {
      setIsResendDisabled(false);
    }
  }, [timer, isResendDisabled]);

  function handleGetOTP() {
    if (isMobileValid)
      getOTP(mobile!, {
        onSuccess: () => {
          setOtpSession(true);

          setTimeout(() => {
            const firstInput = document.querySelector('input[aria-label="Input OTP slot 0"]') as HTMLInputElement;
            if (firstInput) {
              firstInput.focus();
            }
          }, 100);
          setIsResendDisabled(true);
          setTimer(60); // Reset the timer
        },
      });
    else toast({ title: "Please enter a valid mobile number" });
  }

  function handleVerifyOTP() {
    if (otp.length === 4) {
      verifyOTP(
        { otp, requestId: data?.requestId! },
        {
          onSuccess: (data) => {
            localStorage.setItem("mobile", mobile!);
            setMobile(mobile!);
            setToken(data.token, data.refreshToken)
            toast({ title: "Mobile number verified" })
          },
        },
      )
    } else {
      toast({ title: "Please enter a valid OTP" })
    }
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();

    if (!otpSession) handleGetOTP();
    else handleVerifyOTP();
  }

  return (
    <>
    <style>{phoneInputStyles}</style>
    <div className="container flex flex-col h-full">
      <div className="mb-4 flex items-center">
        <div className="relative group">
          <span className="absolute top-full left-1/2 transform -translate-x-1/2 mt-1 hidden group-hover:flex items-center px-2 py-1 rounded text-xs text-white bg-gray-800 shadow-lg">
            Go Back
          </span>
        </div>

        <h1 className="text-sm font-bold mx-auto">Verify mobile number</h1>
      </div>
      <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-1">
          <Label>Mobile Number</Label>
          <PhoneInput
            international
            defaultCountry="IN"
            value={mobile}
            onChange={setMobileVal}
            placeholder="Enter phone number"
            className="PhoneInput"
            countryCallingCodeEditable={false}
            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
              const input = e.target as HTMLInputElement;
              const dialCode = '+' + getCountryCallingCode('IN');
              const dialCodeLength = dialCode.length;
              const selectionStart = input.selectionStart;
              if (e.key === 'Backspace' && selectionStart !== null && selectionStart <= dialCodeLength) {
                e.preventDefault();
              }
            }}
          />
        </div>

        {otpSession && (
          <div className="space-y-1">
            <Label>OTP</Label>
            <InputOTP
              ref={otpInputRef}
              maxLength={4}
              disabled={!otpSession}
              value={otp}
              onChange={(val) => setOtp(val)}
            >
              <InputOTPGroup className="w-full">
                <InputOTPSlot className="w-full" index={0} />
                <InputOTPSlot className="w-full" index={1} />
                <InputOTPSlot className="w-full" index={2} />
                <InputOTPSlot className="w-full" index={3} />
              </InputOTPGroup>
            </InputOTP>
          </div>
        )}

        {otpSession && (
          <div className="flex items-center justify-between">
            <span className="text-sm">
              {isResendDisabled ? `Didn't receive OTP? ${timer}s` : "You can resend OTP now"}
            </span>
            <Button
              type="button"
              variant="link"
              disabled={isResendDisabled}
              onClick={handleGetOTP}
            >
              Resend OTP
            </Button>
          </div>
        )}

        <div className="bottom-5 left-0 w-full space-y-4 max-sm:fixed max-sm:px-4">
          <Button
            type="submit"
            className="w-full font-bold"
            disabled={
              (otpSession ? otp.length !== 4 : !isMobileValid) ||
              isGettingOTP ||
              isVerifying
            }
          >
            {(isGettingOTP || isVerifying) && (
              <Loader2 className="mr-2 size-4 animate-spin" strokeWidth={3} />
            )}
            {!otpSession ? "Get OTP" : "Verify"}
          </Button>
          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={() => onClose()}
          //onClick={() =>  navigate(`/doctors/${doctor?.professionalId}?step=1`)}
          >
            Cancel
          </Button>
        </div>
      </form>
    </div>
    </>
  );
}

export default PopverifyPatient;
